/**
 * Customer dashboard — shows the user's estimates and a CTA to start a
 * new one. Empty state copy when no estimates yet.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { EstimateListItem } from "@/components/estimate/EstimateListItem";
import { listEstimates } from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";
import { useAuthUser } from "@/lib/auth/useAuth";

export function Dashboard() {
  const user = useAuthUser();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await listEstimates({ limit: 25 });
      if (cancelled) return;
      if (!("error" in result)) {
        setEstimates(result.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Your projects
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy md:text-5xl">
        Welcome, <em className="text-starz-gold">{user?.first_name ?? "there"}</em>.
      </h1>
      <div className="mb-8 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-starz-navy">Your estimates</h2>
        <Link
          to="/estimates/new"
          className="inline-flex min-h-[44px] items-center rounded-lg bg-starz-gold px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-navy transition-opacity hover:opacity-90"
        >
          Start a new estimate
        </Link>
      </div>

      {loading ? (
        <p className="text-warm-gray">Loading…</p>
      ) : estimates.length === 0 ? (
        <div className="rounded-xl border border-starz-gold/15 bg-starz-cream p-10 text-center text-warm-gray">
          You don't have any estimates yet. Click{" "}
          <strong className="text-starz-charcoal">Start a new estimate</strong> to chat with Stella
          about your project.
        </div>
      ) : (
        <ul className="space-y-3">
          {estimates.map((est) => (
            <li key={est.estimate_id}>
              <EstimateListItem
                estimate={est}
                onUpdated={(updated) =>
                  setEstimates((prev) =>
                    prev.map((e) => (e.estimate_id === updated.estimate_id ? updated : e)),
                  )
                }
                onDeleted={(estimateId) =>
                  setEstimates((prev) =>
                    prev.filter((e) => e.estimate_id !== estimateId),
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
